if (!customElements.get('shipping-calculator')) {
  class ShippingCalculator extends HTMLElement {
    constructor() {
      super();
      this.zip = this.querySelector('.js-zip-input');
      this.zip.addEventListener('keydown', this.handleZipKeydown.bind(this));
      this.button = this.querySelector('button');
      this.button.addEventListener('click', this.handleSubmit.bind(this));

      // Sacola: AUTO-COTA ao abrir o drawer se já há CEP lembrado (cima-cep-memory) —
      // quem calculou numa tela não deve ter que recalcular na outra.
      if (this.hasAttribute('data-selectable')) {
        const drawer = this.closest('[data-name="cart-drawer"]');
        const tryAuto = () => {
          if (this._autoQuoted) return;
          const aberto = !drawer || drawer.getAttribute('aria-hidden') === 'false';
          if (aberto && (this.zip.value || '').replace(/\D/g, '').length === 8) {
            this._autoQuoted = true;
            this.handleSubmit(new Event('auto'));
          }
        };
        if (drawer && window.MutationObserver) {
          new MutationObserver(tryAuto).observe(drawer, { attributes: true, attributeFilter: ['aria-hidden'] });
        }
        setTimeout(tryAuto, 250); // drawer já aberto (re-render AJAX de qtd/cupom)

        // RECOTA quando o carrinho MUDA (qtd/remoção disparam este evento): sem isso a
        // cotação fica STALE — faixa de peso e total viram foto velha do carrinho
        // (bug real 01/ago: 2001 tijolos exibindo cotação de 58 kg feita antes).
        document.addEventListener('dispatch:cart-drawer:refresh', () => {
          if (!this.isConnected) return;
          clearTimeout(this._rqT);
          this._rqT = setTimeout(() => {
            if (this._quoted && (this.zip.value || '').replace(/\D/g, '').length === 8) {
              this.handleSubmit(new Event('auto'));
            }
          }, 900);
        });
      }
    }

    init() {
      this.country = this.querySelector('.js-country-select');
      this.province = this.querySelector('.js-province-select');
      this.rates = this.querySelector('.js-rates');
      this.errors = this.querySelector('.js-errors');
      this.initialised = true;
    }

    /**
     * Handles submission of the rates calculator.
     * @param {object} evt - Event object.
     */
    async handleSubmit(evt) {
      evt.preventDefault();
      if (!this.initialised) this.init();

      this._lastAdded = null; // linhas cotadas no modo-produto (preenchido pelo safeAdd)
      this.toggleNote(false); // volta a mostrar a nota "Estimativa com base nos itens da sua sacola"
      this.errors.hidden = true;
      this.rates.hidden = true;
      this.button.disabled = true;
      this.button.classList.add('is-loading');

      // Fluxo ASSÍNCRONO (igual ao checkout e à página do Carrinho): o síncrono
      // /shipping_rates.json NÃO espera carrier-calculated (Frenet, Cimafer POA) e
      // vinha vazio. Aqui: CEP → ViaCEP (UF, exigida no BR) → prepare → poll async.
      //
      // MODO PRODUTO (data-mode="product", só na PDP): tenta cotar SÓ o produto da
      // página (salva→limpa→adiciona→cota→restaura o carrinho). ⚠️ 22/jul: essa dança
      // já quebrou em produção 1x (retornou "sem rates" pra um CEP que cota normal) —
      // causa não 100% confirmada (suspeita: race/eventual-consistency do cart do
      // Shopify ou rate-limit do storefront sob a rajada de requests). Por isso:
      // REDE DE SEGURANÇA — se o modo-produto vier vazio ou falhar por qualquer
      // motivo, SEMPRE cai pro modo-carrinho (nunca mostra "não enviamos" à toa).
      const zip = (this.zip.value || '').replace(/\D/g, '');

      try {
        if (zip.length !== 8) { this.showErrors({ zip: 'Informe um CEP válido (8 dígitos).' }); return; }

        let uf = '', city = '', bairro = '';
        let vcFalhou = false;
        try {
          const vc = await fetch('https://viacep.com.br/ws/' + zip + '/json/', { signal: (function () { var c = new AbortController(); setTimeout(function () { c.abort(); }, 4000); return c.signal; })() }).then((r) => r.json());
          if (vc && !vc.erro) { uf = vc.uf || ''; city = vc.localidade || ''; bairro = vc.bairro || ''; }
        } catch (e) { vcFalhou = true; /* ViaCEP indisponível → segue sem UF */ }
        if (!uf && this.province && this.province.value) uf = this.province.value;
        if (!uf && Number(zip) >= 90000000 && Number(zip) <= 99999999) uf = 'RS'; // faixa de CEP do RS (ViaCEP fora do ar)
        if (!uf) { this.showErrors({ zip: vcFalhou ? 'Não conseguimos consultar o CEP agora. Tente de novo em instantes.' : 'CEP não encontrado — confira o número.' }); return; }

        let rates = [];
        let usedFallback = false;
        let produtoVazio = false; // cotação do PRÓPRIO produto veio vazia (antes do fallback pro carrinho)
        if (this.dataset.mode === 'product') {
          rates = await this.quoteProductOnlySafe(uf);
          if (!rates.length) {
            usedFallback = true;
            produtoVazio = true;
            // Carrinho vazio (a dança do modo-produto devolve o carrinho como estava) → recotar não adianta
            // e gastava ~11 s em 18 tentativas 422. Só recota se houver algo no carrinho.
            const _cj = await fetch(theme.routes.cart + '.js').then((r) => r.json()).catch(() => null);
            rates = (_cj && Array.isArray(_cj.items) && _cj.items.length === 0)
              ? [] : await this.fetchRates(uf); // rede de segurança: nunca mostrar "sem rates" à toa
          }
        } else {
          rates = await this.fetchRates(uf);
        }

        // Peso do que foi cotado (p/ a dica de faixa do carrier Cimafer):
        // modo-produto = grams da variante × qty; senão = peso total do carrinho.
        let pesoG = 0;
        try {
          if (this.dataset.mode === 'product' && !usedFallback) {
            const line = this.getProductLine();
            pesoG = Number(this.dataset.grams || 0) * ((line && line.quantity) || 1);
          } else {
            const c = await fetch(theme.routes.cart + '.js').then((r) => r.json());
            pesoG = Number(c.total_weight) || 0;
            this._cartCents = Number(c.total_price) || 0; // p/ o total vivo do select (Sacola)
          }
        } catch (e) { /* sem peso → só não mostra a dica de faixa */ }

        this._quoted = true; // marca que já cotou (habilita a recotação automática)

        // CARGA GRANDE fora da Grande POA (06/out): cotação VAZIA + item carga:grande = o item pesado
        // derrubou o frete do carrinho todo. Em vez de "Não enviamos para este destino", explica QUAL item
        // bloqueia e as saídas (cima-frete-carga.js → Worker /api/leves). Falhou/sem carga grande → fluxo normal.
        let bloqueio = null;
        // PDP de produto carga:grande (data-carga-grande) cujo PRÓPRIO frete veio vazio: explica o PRODUTO,
        // mesmo que o fallback do carrinho tenha achado frete de OUTROS itens (não é o frete deste produto).
        const pdpCargaGrandeSemFrete = this.dataset.mode === 'product' && produtoVazio && (this.hasAttribute('data-carga-grande') || this.hasAttribute('data-sem-correios'));
        if ((pdpCargaGrandeSemFrete || !(rates && rates.length)) && window.cimaFreteCarga) {
          try {
            let prod = null;
            if (this.dataset.mode === 'product' && (!usedFallback || pdpCargaGrandeSemFrete)) {
              const ln = this.getProductLine();
              const h1 = document.querySelector('h1');
              if (ln) prod = { id: ln.id, quantity: ln.quantity, grams: Number(this.dataset.grams || 0), titulo: h1 ? h1.textContent.trim() : '' };
            }
            bloqueio = await window.cimaFreteCarga.analisar({ zip: zip, produto: prod });
          } catch (e) { bloqueio = null; }
        }
        if (bloqueio && bloqueio.tipo === 'incerto' && rates && rates.length) bloqueio = null; // há frete: não esconder
        if (bloqueio) this.showBloqueio(bloqueio);
        else this.showRates({ shipping_rates: rates || [] }, usedFallback, pesoG);
        // GA4: view_shipping_info (custom) — dispara em qualquer cálculo de frete.
        if (window.cimaShippingInfo) {
          // O QUE foi cotado (dono 01/out): no modo-produto a cotação é do produto da PDP,
          // não da sacola (que pode estar vazia ou ter outras coisas) → manda peso/valor/itens
          // do produto. Sem isso o evento gravava peso e total = 0 em toda cotação de PDP.
          const soProduto = this.dataset.mode === 'product' && !usedFallback;
          let quoted = null;
          if (soProduto && this._lastAdded && this._lastAdded.length) {
            const its = this._lastAdded;
            quoted = {
              item_count: its.reduce((s, it) => s + (it.quantity || 1), 0),
              total_price: its.reduce((s, it) => s + (it.final_line_price != null ? it.final_line_price : (it.line_price || 0)), 0),
              total_weight: its.reduce((s, it) => s + (it.grams || 0) * (it.quantity || 1), 0),
              items: its,
            };
          }
          window.cimaShippingInfo({
            zip: zip, uf: uf, city: city, bairro: bairro, rates: rates || [],
            context: soProduto ? 'produto' : 'sacola', quoted: quoted,
          });
        }
      } catch (error) {
        console.log(error); // eslint-disable-line
      } finally {
        this.button.classList.remove('is-loading');
        this.button.disabled = false;
      }
    }

    /**
     * Dispara o cálculo assíncrono do carrinho ATUAL e faz poll até as taxas ficarem prontas.
     * @param {string} uf - Sigla do estado (province) exigida no BR.
     * @returns {Promise<Array>} Lista de shipping_rates (pode ser vazia).
     */
    async fetchRates(uf) {
      const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
      const addr = 'shipping_address%5Bzip%5D=' + (this.zip.value || '').replace(/\D/g, '') +
                   '&shipping_address%5Bcountry%5D=BR' +
                   '&shipping_address%5Bprovince%5D=' + encodeURIComponent(uf);
      await fetch(theme.routes.cart + '/prepare_shipping_rates.json?' + addr, { method: 'POST' }).catch(() => {});
      // Exige a MESMA lista (por nome de carrier) em 2 consultas seguidas antes de
      // aceitar como definitiva — protege contra pegar um snapshot parcial caso o
      // endpoint assíncrono devolva um carrier (ex.: Loggi via Frenet) atrasado.
      let candidate = null;
      for (let i = 0; i < 18; i++) {
        const r = await fetch(theme.routes.cart + '/async_shipping_rates.json', { headers: { Accept: 'application/json' } });
        if (r.ok) {
          const d = await r.json();
          if (d && Array.isArray(d.shipping_rates)) {
            const sig = d.shipping_rates.map((x) => x.name).sort().join('|');
            if (candidate && candidate.sig === sig) return d.shipping_rates; // estável 2x seguidas
            candidate = { sig, rates: d.shipping_rates };
          }
        }
        await sleep(600);
      }
      return candidate ? candidate.rates : []; // esgotou tentativas: devolve o último snapshot visto
    }

    /**
     * Lê a variante e a quantidade selecionadas no formulário de produto da PDP.
     * @returns {?{id:number, quantity:number}}
     */
    getProductLine() {
      const form = document.querySelector('form[action*="/cart/add"]');
      let id = null;
      let qty = 1;
      if (form) {
        const idEl = form.querySelector('[name="id"]');
        if (idEl && idEl.value) id = idEl.value;
        const qEl = form.querySelector('[name="quantity"]');
        if (qEl && Number(qEl.value) > 0) qty = Number(qEl.value);
      }
      if (!id) id = new URL(window.location.href).searchParams.get('variant');
      return id ? { id: Number(id), quantity: qty } : null;
    }

    /**
     * Tenta cotar APENAS o produto da PDP sem alterar o carrinho do cliente. Toda
     * etapa é verificada; qualquer falha aborta com segurança (retorna [] e o
     * chamador cai no fallback de carrinho — nunca deixa o cliente com "sem rates").
     * @param {string} uf - UF para a cotação.
     * @returns {Promise<Array>}
     */
    async quoteProductOnlySafe(uf) {
      const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
      const line = this.getProductLine();
      if (!line) return [];

      const cartUrl = theme.routes.cart;
      let saved;
      try {
        saved = await fetch(cartUrl + '.js').then((r) => r.json());
      } catch (e) { return []; }

      // Carrinho já vazio (ou só este produto) → não há risco de estado a preservar,
      // pode cotar direto sem a dança de salvar/restaurar.
      const jaSoEsteProduto = saved.items.length === 0 ||
        (saved.items.length === 1 && String(saved.items[0].variant_id) === String(line.id));
      if (jaSoEsteProduto) {
        if (saved.items.length === 0) {
          const addOk = await this.safeAdd(cartUrl, [{ id: line.id, quantity: line.quantity }]);
          if (!addOk) return [];
          const rates = await this.fetchRates(uf);
          await this.safeClear(cartUrl); // devolve vazio, como estava
          return rates;
        }
        // já é só este produto (mesma variante) → cota o carrinho como está, sem mexer
        return await this.fetchRates(uf);
      }

      // Carrinho tem OUTRAS coisas → salva, cota só o produto, restaura. Verifica
      // cada passo; qualquer coisa fora do esperado aborta e restaura na hora.
      try {
        const cleared = await this.safeClear(cartUrl);
        if (!cleared) return [];

        const addOk = await this.safeAdd(cartUrl, [{ id: line.id, quantity: line.quantity }]);
        if (!addOk) { await this.restoreCart(cartUrl, saved); return []; }

        // pequena folga antes de cotar — mitiga qualquer atraso de propagação do cart
        await sleep(350);

        // confere que o carrinho realmente está com SÓ o produto antes de cotar
        const check = await fetch(cartUrl + '.js').then((r) => r.json()).catch(() => null);
        const okState = check && check.items.length === 1 && String(check.items[0].variant_id) === String(line.id);
        if (!okState) { await this.restoreCart(cartUrl, saved); return []; }

        const rates = await this.fetchRates(uf);
        const restored = await this.restoreCart(cartUrl, saved);
        if (!restored) {
          // não deixa isso silencioso — evento p/ investigar depois, sem alarmar o cliente
          if (window.cimaShippingInfo) window.cimaShippingInfo({ error: 'restore_falhou' });
        }
        return rates;
      } catch (e) {
        await this.restoreCart(cartUrl, saved).catch(() => {});
        return [];
      }
    }

    /** @returns {Promise<boolean>} true se o clear realmente esvaziou o carrinho */
    async safeClear(cartUrl) {
      try {
        await fetch(cartUrl + '/clear.js', { method: 'POST' });
        const c = await fetch(cartUrl + '.js').then((r) => r.json());
        return c.items.length === 0;
      } catch (e) { return false; }
    }

    /** @returns {Promise<boolean>} true se o add realmente confirmou os itens (sem 4xx/erro) */
    async safeAdd(cartUrl, items) {
      try {
        const r = await fetch(cartUrl + '/add.js', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: items }),
        });
        if (!r.ok) return false;
        const d = await r.json();
        // Guarda as linhas que o add.js devolveu (preço, grams, sku, título) — é o que
        // foi COTADO no modo-produto; vai pro view_shipping_info (a sacola é restaurada
        // logo depois e o /cart.js não mostra mais o produto cotado).
        if (d && (d.items || d.id)) this._lastAdded = d.items || [d];
        return !!(d && (d.items || d.id));
      } catch (e) { return false; }
    }

    /**
     * Restaura o carrinho salvo e CONFERE que bateu (quantidade de linhas e total).
     * @returns {Promise<boolean>}
     */
    async restoreCart(cartUrl, saved) {
      try {
        await fetch(cartUrl + '/clear.js', { method: 'POST' });
        if (saved.items && saved.items.length) {
          const items = saved.items.map((it) => {
            const o = { id: it.id, quantity: it.quantity };
            if (it.properties && Object.keys(it.properties).length) o.properties = it.properties;
            if (it.selling_plan_allocation && it.selling_plan_allocation.selling_plan) {
              o.selling_plan = it.selling_plan_allocation.selling_plan.id;
            }
            return o;
          });
          await fetch(cartUrl + '/add.js', {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items: items }),
          });
        }
        const now = await fetch(cartUrl + '.js').then((r) => r.json());
        return now.item_count === saved.item_count && now.total_price === saved.total_price;
      } catch (e) { return false; }
    }

    /**
     * Formata o prazo de entrega de uma taxa. Alguns carriers (ex.: nosso "Cargas
     * Pesadas") não devolvem prazo por CEP → nesse caso retorna string vazia.
     * @param {object} rate - Objeto de shipping rate.
     * @returns {string}
     */
    formatEta(rate) {
      const d = rate.delivery_days;
      if (Array.isArray(d) && d.length && d[0] != null) {
        const min = d[0];
        const max = d.length > 1 && d[1] != null ? d[1] : d[0];
        if (min === max) return min === 1 ? '1 dia útil' : `${min} dias úteis`;
        return `${min}–${max} dias úteis`;
      }
      // Nossos carriers (Correios direto, Grandes Cargas) mandam o prazo no
      // description — a Shopify NÃO converte isso em delivery_days (verificado
      // 14/set no async_shipping_rates.json: delivery_days:[] + description
      // "5 dias úteis"). Extrai só o trecho de prazo do texto.
      const m = String(rate.description || '').match(/(?:até\s+)?\d+(?:\s*[–-]\s*\d+)?\s*dias?\s+út(?:eis|il)/i);
      return m ? m[0] : '';
    }

    /**
     * Formats and shows a list of available shipping rates.
     * @param {object} data - Response data.
     * @param {boolean} [isCartFallback] - true quando o modo-produto falhou e isto é a cotação do carrinho.
     * @param {number} [pesoGrams] - peso (g) do que foi cotado, p/ a dica de faixa do carrier Cimafer.
     */
    showRates(data, isCartFallback, pesoGrams) {
      const headingEl = this.rates.querySelector('.js-rates-heading');
      const textEl = this.rates.querySelector('.js-rates-text');
      textEl.innerHTML = '';

      // Grandes Cargas (04/ago/2026): item volumoso (tag carga:grande) não viaja por
      // Correios/parcela leve. Espelha a Delivery Customization Function do checkout:
      // se a cotação tem a NOSSA entrega → só ela; senão → derruba só Correios-like
      // (transportadora fora de POA continua). Sem o atributo, nada muda.
      if (this.hasAttribute('data-carga-grande') && data.shipping_rates && data.shipping_rates.length) {
        const NOSSA = /grandes\s*cargas|cimafer/i;
        const CORREIOS = /sedex|\bpac\b|correios|mini\s*envios|loggi/i;
        const temNossa = data.shipping_rates.some((r) => NOSSA.test(r.name || ''));
        data = Object.assign({}, data, {
          shipping_rates: data.shipping_rates.filter((r) => (temNossa
            ? NOSSA.test(r.name || '')
            : !CORREIOS.test(r.name || ''))),
        });
      }

      // Entrega expressa (Uber) — prioridade do dono (06/ago): fica ACIMA de
      // todas as outras entregas e em NEGRITO. A Retirada segue no topo (grátis).
      const UBER_RX = /entrega expressa|uber/i;
      if (data.shipping_rates && data.shipping_rates.length) {
        data = Object.assign({}, data, {
          shipping_rates: data.shipping_rates.slice().sort((a, b) =>
            (UBER_RX.test(b.name || '') ? 1 : 0) - (UBER_RX.test(a.name || '') ? 1 : 0)),
        });
      }

      // Pedido mínimo do Uber = R$50 (regra do dono, 07/ago; o worker não cota
      // abaixo). Quando o CEP é de Porto Alegre e o carrinho está perto,
      // mostrar QUANTO FALTA pra liberar — vende o upsell sozinho.
      const cepNum = parseInt((this.zip && this.zip.value || '').replace(/\D/g, ''), 10) || 0;
      const temUber = (data.shipping_rates || []).some((r) => UBER_RX.test(r.name || ''));
      const uberHint = (!temUber && cepNum >= 90000000 && cepNum <= 91999999
        && this._cartCents > 0 && this._cartCents < 5000)
        ? '<p class="ship-uber-hint" style="margin:10px 0 0;padding:8px 10px;border:1px dashed #9C2A21;border-radius:8px;font-size:13px;line-height:1.4">🛵 Faltam <b style="color:#9C2A21">R$ '
          + ((5000 - this._cartCents) / 100).toFixed(2).replace('.', ',')
          + '</b> em produtos para liberar a <b>Entrega expressa hoje (Uber)</b>.</p>'
        : '';

      // Retirada grátis: sempre visível no resultado — é o plano B de quem não tem
      // cotação ou acha o frete caro (item pesado), e antes ficava escondida.
      // data-no-pickup (Sacola): o drawer já tem o cartão "Retirar na loja" acima — não duplicar.
      // Bloco PRÓPRIO, separado da entrega (19/set): antes vinha colado na lista de
      // taxas e lia como mais uma opção de frete ("Frete grátis" × "Retirada grátis").
      const retirada = this.hasAttribute('data-no-pickup') ? ''
        : '<div class="ship-block ship-block--pickup">'
          + '<p class="ship-block__label">Ou retire na loja</p>'
          + '<div class="ship-row"><span class="ship-row__name">Loja Cimafer · Porto Alegre</span><b class="ship-row__price">Grátis</b></div>'
          + '<p class="ship-row__meta">Pronta em ~2h · escolha “Retirada” no checkout</p>'
          + '</div>';
      const cepFmt = (this.zip && this.zip.value || '').replace(/\D/g, '').replace(/^(\d{5})(\d{3})$/, '$1-$2');
      const entregaLabel = `<p class="ship-block__label">Entrega${cepFmt ? ' no CEP ' + cepFmt : ''}</p>`;

      if (data.shipping_rates && data.shipping_rates.length) {
                let rates = '';

        data.shipping_rates.forEach((rate) => {
          const preco = Number(rate.price) === 0 ? 'Grátis' : 'R$ ' + Number(rate.price).toFixed(2).replace('.', ',');
          const eta = this.formatEta(rate);
          const destaque = UBER_RX.test(rate.name || '') ? ' style="font-weight:700"' : '';
          rates += `<li class="ship-rate"${destaque}><div class="ship-row"><span class="ship-row__name">${rate.name}</span><b class="ship-row__price">${preco}</b></div>`
            + (eta ? `<p class="ship-row__meta">${eta}</p>` : '') + '</li>';
        });

        // Dica da FAIXA de peso — só quando a cotação inclui o nosso carrier por bandas
        // ("Cimafer · Cargas Pesadas"). O preço dele é por faixa: encher a faixa não
        // muda o frete, e isso vende ("cabem mais N unidades pelo mesmo valor").
        // ⚠️ TIERS replicadas de cimafer-repo/frete-poa/lib/tabela.js (TIERS_KG) — manter em sincronia.
        let faixaHtml = '';
        const nossa = data.shipping_rates.find((r) => /cargas pesadas|grandes cargas/i.test(r.name || ''));
        if (nossa && pesoGrams > 0) {
          const TIERS = [300, 1000, 2500, 4000, 7000, 10000];
          const kg = pesoGrams / 1000;
          const teto = TIERS.find((t) => kg <= t);
          if (teto) {
            const sobraKg = Math.floor(teto - kg);
            if (sobraKg >= 5) {
              const unG = Number(this.dataset.grams || 0);
              const extraUn = (this.dataset.mode === 'product' && !isCartFallback && unG > 0)
                ? ` — dá pra levar ~<b>+${Math.floor((sobraKg * 1000) / unG).toLocaleString('pt-BR')} un</b> deste produto sem pagar mais frete`
                : ` — ainda cabem ~<b>${sobraKg.toLocaleString('pt-BR')} kg</b> pelo mesmo valor`;
              faixaHtml = `<p class="ship-faixa">💡 A entrega Cimafer cobre até <b>${teto.toLocaleString('pt-BR')} kg</b> nesse preço${extraUn}.</p>`;
            }
          }
        }

        // data-selectable (Sacola): opções viram RADIOS — retirada grátis + cotações —
        // com linha de total viva conforme a escolha. (A escolha efetiva é no checkout;
        // aqui é preview honesto com os mesmos valores.)
        if (this.hasAttribute('data-selectable')) {
          let rows = '<label class="ship-sel"><input type="radio" name="ship-sel" value="0" data-sel="retirada" checked>'
            + '<span class="ship-name">Retirar na loja — Porto Alegre</span><b>Grátis</b></label>';
          data.shipping_rates.forEach((rate) => {
            const cents = Math.round(Number(rate.price) * 100);
            const preco = cents === 0 ? 'Grátis' : 'R$ ' + Number(rate.price).toFixed(2).replace('.', ',');
            const eta = this.formatEta(rate);
            const etaHtml = eta ? ` <span class="ship-eta">· ${eta}</span>` : '';
            const negrito = UBER_RX.test(rate.name || '') ? ' style="font-weight:700"' : '';
            rows += `<label class="ship-sel"${negrito}><input type="radio" name="ship-sel" value="${cents}" data-sel="entrega" data-rname="${(rate.name || '').replace(/"/g, '')}">`
              + `<span class="ship-name">${rate.name}${etaHtml}</span><b>${preco}</b></label>`;
          });
          const totalLine = this._cartCents > 0
            ? '<p class="ship-sel-total">Total <span data-sel-mode>retirando na loja</span>: <b data-sel-total></b></p>' : '';
          headingEl.hidden = true;
          textEl.innerHTML = rows + faixaHtml + uberHint + totalLine;
          // restaura a última escolha do cliente (persistida entre Sacola ↔ /cart)
          let savedSel = '';
          try { savedSel = localStorage.getItem('cima:frete-sel') || ''; } catch (e) { /* noop */ }
          if (savedSel && savedSel !== 'retirada') {
            const m = Array.prototype.find.call(textEl.querySelectorAll('input[name="ship-sel"]'), (i) => i.dataset.rname === savedSel);
            if (m) m.checked = true;
          }
          const fmtC = (c) => 'R$ ' + (c / 100).toFixed(2).replace('.', ',');
          const upd = () => {
            const sel = textEl.querySelector('input[name="ship-sel"]:checked');
            const totEl = textEl.querySelector('[data-sel-total]');
            if (!sel) return;
            if (totEl) {
              textEl.querySelector('[data-sel-mode]').textContent = sel.dataset.sel === 'retirada' ? 'retirando na loja' : 'com entrega';
              totEl.textContent = fmtC(this._cartCents + Number(sel.value));
            }
          };
          textEl.querySelectorAll('input[name="ship-sel"]').forEach((i) => i.addEventListener('change', () => {
            try { localStorage.setItem('cima:frete-sel', i.dataset.rname || 'retirada'); } catch (e) { /* noop */ }
            upd();
          }));
          upd();
          this.rates.hidden = false;
          return;
        }

        // Sem o heading genérico ("Existe uma taxa de envio…") — contradizia a linha "Grátis".
        headingEl.hidden = true;
        const nota = isCartFallback ? '<p class="ship-note-fallback">Estimativa com base nos itens da sua sacola.</p>' : '';
        textEl.innerHTML = `<div class="ship-block ship-block--delivery">${entregaLabel}<ul class="ship-rates">${rates}</ul>${faixaHtml}${uberHint}${nota}</div>${retirada}`;
      } else {
        headingEl.hidden = true;
        textEl.innerHTML = `<div class="ship-block ship-block--delivery">${entregaLabel}<p class="ship-row__meta">${theme.strings.shippingCalculator.noRates}</p>${uberHint}</div>${retirada}`;
      }

      this.rates.hidden = false;
    }

    /** Carga grande fora da Grande POA: mostra o item que bloqueia + saídas (ver cima-frete-carga.js). */
    /** Esconde/mostra a nota fixa da PDP ("Estimativa com base nos itens da sua sacola") — contradiz a mensagem de bloqueio. */
    toggleNote(esconder) {
      const root = this.closest('.cima-pdp__ship') || this.parentElement;
      const note = root && root.querySelector('.cima-pdp__ship-note');
      if (note) note.hidden = !!esconder;
    }

    showBloqueio(an) {
      this.toggleNote(true);
      const headingEl = this.rates.querySelector('.js-rates-heading');
      const textEl = this.rates.querySelector('.js-rates-text');
      headingEl.hidden = true;
      textEl.innerHTML = '';
      // remover item: Sacola → o drawer re-renderiza e a recotação automática roda sozinha;
      // PDP → o produto não está (necessariamente) na sacola, sem botão de remover.
      const onRemovido = this.hasAttribute('data-selectable')
        ? () => document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh'))
        : () => { window.location.reload(); };
      textEl.appendChild(window.cimaFreteCarga.render(an, onRemovido));
      this.rates.hidden = false;
    }

    /**
     * Handles keydown event on the zip code input.
     * @param {object} evt - Event object.
     */
    handleZipKeydown(evt) {
      if (evt.key === 'Enter') {
        evt.preventDefault();
        this.handleSubmit(evt);
      }
    }

    /**
     * Formats and shows errors relating to rates retrieval.
     * @param {object} data - Response data.
     */
    showErrors(data) {
      let errors = '';

      Object.keys(data).forEach((key) => {
        errors += `<li>${data[key]}</li>`;
      });

      this.errors.querySelector('.js-errors-text').innerHTML = `<ul class="styled-list">${errors}</ul>`;
      this.errors.hidden = false;
    }
  }

  customElements.define('shipping-calculator', ShippingCalculator);
}
